import { useState, useEffect, useRef } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { ColDef } from './useColumnPrefs'

// ── Resize-aware SortableColHeader ──────────────────────────────────────────

interface SortableColHeaderProps {
  id: string
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  onClick?: () => void
  /** Called with final px width when the user releases the resize handle */
  onResizeEnd?: (newWidth: number) => void
  /** Called when the user double-clicks the resize handle (reset to auto-fit) */
  onResizeReset?: () => void
  /** Minimum column width in px (default 50) */
  minWidth?: number
}

export function SortableColHeader({
  id, children, className, style, onClick,
  onResizeEnd, onResizeReset, minWidth = 50,
}: SortableColHeaderProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const thRef  = useRef<HTMLTableCellElement | null>(null)
  const resize = useRef<{ pointerId: number; startX: number; startW: number } | null>(null)

  return (
    <th
      ref={el => { setNodeRef(el); thRef.current = el }}
      style={{
        ...style,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        position: 'relative',
        zIndex: isDragging ? 10 : undefined,
      }}
      className={className}
      onClick={onClick}
    >
      <div className="flex items-center gap-0.5 group/col">
        <span
          {...attributes}
          {...listeners}
          className="opacity-0 group-hover/col:opacity-40 hover:!opacity-100 cursor-grab active:cursor-grabbing text-gray-400 select-none shrink-0 text-sm leading-none"
          title="Drag to reorder"
          onClick={e => e.stopPropagation()}
        >
          ⠿
        </span>
        {children}
      </div>

      {onResizeEnd && (
        <div
          className="absolute right-0 top-0 h-full w-1 hover:bg-blue-400/60 active:bg-blue-500/60"
          style={{ cursor: 'col-resize', zIndex: 2, touchAction: 'none' }}
          title="Drag to resize · Double-click to auto-fit"
          onClick={e => e.stopPropagation()}
          onPointerDown={e => {
            e.preventDefault()
            e.stopPropagation()
            ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
            resize.current = {
              pointerId: e.pointerId,
              startX:    e.clientX,
              startW:    thRef.current?.offsetWidth ?? (typeof style?.width === 'number' ? style.width : 100),
            }
          }}
          onPointerMove={e => {
            if (!resize.current || resize.current.pointerId !== e.pointerId) return
            const newW = Math.max(minWidth, resize.current.startW + e.clientX - resize.current.startX)
            if (thRef.current) thRef.current.style.width = `${newW}px`
          }}
          onPointerUp={e => {
            if (!resize.current || resize.current.pointerId !== e.pointerId) return
            ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
            const newW = Math.max(minWidth, resize.current.startW + e.clientX - resize.current.startX)
            resize.current = null
            onResizeEnd(newW)
          }}
          onPointerCancel={() => { resize.current = null }}
          onDoubleClick={e => {
            e.stopPropagation()
            onResizeReset?.()
          }}
        />
      )}
    </th>
  )
}

// ── Auto-fit width computation (canvas measureText, no DOM) ─────────────────

let _cvs: HTMLCanvasElement | null = null
function measurePx(text: string, bold = false): number {
  if (typeof document === 'undefined') return text.length * 7
  if (!_cvs) _cvs = document.createElement('canvas')
  const ctx = _cvs.getContext('2d')
  if (!ctx) return text.length * 7
  ctx.font = bold ? 'bold 12px sans-serif' : '12px sans-serif'
  return ctx.measureText(text).width
}

/**
 * Compute column widths so every header label fits without truncation.
 * Optionally pass extra data strings per column to widen based on content too.
 * Returns widths clamped to [minW, maxW].
 */
export function computeAutoFitWidths(
  cols: readonly ColDef[],
  opts?: {
    minWidths?: Record<string, number>  // per-column min (default 50)
    maxWidths?: Record<string, number>  // per-column max (default 400)
    skipIds?:   Set<string>             // skip these columns entirely
    /** Extra data strings to measure per column (beyond header text) */
    dataSamples?: Record<string, string[]>
  }
): Record<string, number> {
  const { minWidths = {}, maxWidths = {}, skipIds = new Set(), dataSamples = {} } = opts ?? {}
  const ICON_PAD = 20   // drag handle icon
  const CELL_PAD = 20   // left+right cell padding
  const result: Record<string, number> = {}

  for (const col of cols) {
    if (skipIds.has(col.id)) continue
    let w = measurePx(col.label, true) + ICON_PAD + CELL_PAD
    for (const s of (dataSamples[col.id] ?? [])) {
      w = Math.max(w, measurePx(s) + CELL_PAD)
    }
    const minW = minWidths[col.id] ?? 50
    const maxW = maxWidths[col.id] ?? 400
    result[col.id] = Math.max(minW, Math.min(maxW, Math.ceil(w)))
  }
  return result
}

// ── ColumnToggleButton ───────────────────────────────────────────────────────

interface ColumnToggleButtonProps {
  cols: readonly ColDef[]
  hidden: string[]
  onToggle: (id: string) => void
  onReset: () => void
  /** If provided, shows an "Auto-fit widths" button */
  onRecalculate?: () => void
}

export function ColumnToggleButton({ cols, hidden, onToggle, onReset, onRecalculate }: ColumnToggleButtonProps) {
  const [open, setOpen] = useState(false)
  const [pos,  setPos]  = useState<{ top: number; right: number } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const btnRef       = useRef<HTMLButtonElement>(null)

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
    }
    setOpen(o => !o)
  }

  useEffect(() => {
    if (!open) return
    // pointerdown (not click/mousedown) with an explicit target-containment check: a scrollbar
    // click's target is the scrollable element itself, which IS inside containerRef (the
    // dropdown's own overflow-y-auto div is a DOM descendant of it), so this already treats a
    // scrollbar click as "inside" -- pointerdown also unifies mouse/touch/pen instead of only
    // firing for mouse.
    const onOutside = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    // Z39: closes on page/ancestor scroll since the menu is `position: fixed` at a top/right
    // captured once on open -- it'd otherwise float away from the button. But `scroll` doesn't
    // bubble, so this has to listen in the capture phase to catch it at all, which means it
    // also catches the menu's OWN internal scroll (24 columns need one) -- closing the menu the
    // instant you tried to scroll to see the rest of the list. Ignore scrolls whose target is
    // inside the menu itself; anything else (the real page/ancestor scrolling) still closes it.
    const onScroll = (e: Event) => {
      if (containerRef.current && containerRef.current.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('pointerdown', onOutside)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('pointerdown', onOutside)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open])

  const visCount = cols.filter(c => !hidden.includes(c.id)).length

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={btnRef}
        onClick={toggle}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 whitespace-nowrap"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        Columns ({visCount}/{cols.length})
      </button>
      {open && pos && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-3 min-w-[180px] max-w-[280px] max-h-[60vh] overflow-y-auto"
          style={{ top: pos.top, right: pos.right }}
        >
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Show / Hide</div>
          {cols.map(col => (
            <label key={col.id}
              className="flex items-center gap-2 py-1 cursor-pointer text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white select-none">
              <input type="checkbox" checked={!hidden.includes(col.id)} onChange={() => onToggle(col.id)} className="rounded" />
              {col.label}
            </label>
          ))}
          <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2 space-y-1">
            {onRecalculate && (
              <button
                onClick={() => { onRecalculate(); setOpen(false) }}
                className="block w-full text-left text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
              >
                Auto-fit column widths
              </button>
            )}
            <button
              onClick={() => { onReset(); setOpen(false) }}
              className="block w-full text-left text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400 hover:underline"
            >
              Reset to default
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
